import { Fragment, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { DurationMetric, SentinelPerformanceRow, SentinelTurnBucket } from '@/types/sentinelAnalytics';
import { AnalyticsTable, Cell, DIVIDER, EdgeCell, EdgeHead, ExpandToggle, GroupHead, Head, STICKY_CELL, STICKY_HEAD, SUB_ROW_NAME } from './analytics-table';
import { subRowProps, useExpandedRows } from './use-expanded-rows';
import { useSortedRows } from './use-sorted-rows';
import { formatCount, formatPercent, formatWait } from './format';

const BUCKETS: SentinelTurnBucket[] = ['automation', 'hitl', 'oversight'];
const BUCKET_LABEL: Record<SentinelTurnBucket, string> = { automation: 'Automation', hitl: 'HITL', oversight: 'Oversight' };

/** One bucket row of a category or a channel. */
type Row = Omit<SentinelPerformanceRow, 'category'> & { name: string };

interface Group {
  name: string;
  buckets: Array<Row | undefined>;
}

type SortKey = 'name' | 'tickets' | 'aiAccuracy' | 'csat';

interface PerformanceTableProps {
  /** First column header, e.g. "Category". */
  nameLabel: string;
  rows: Row[];
  /** Fixed rows, in order, even with no tickets; defaults to the names found in `rows`. */
  names?: string[];
  businessAvailable: boolean;
  /** Each name's sub-rows (a category's sub-categories), opened from the name. */
  subRows?: Record<string, Row[]>;
}

function averageDuration(metric: DurationMetric | undefined, businessAvailable: boolean): string {
  const stats = metric ? (businessAvailable ? metric.business : metric.calendar) : null;
  return stats ? formatWait(stats.averageMs, businessAvailable ? metric?.calendar?.averageMs : undefined).replace(' ', '') : '-';
}

function namesOf(rows: Row[]): string[] {
  return [...new Set(rows.map((row) => row.name))];
}

function groupByName(rows: Row[], names: string[]): Group[] {
  return names.map((name) => ({ name, buckets: BUCKETS.map((bucket) => rows.find((row) => row.name === name && row.bucket === bucket)) }));
}

/** Row-level value for sorting, rolled up from its bucket rows. */
function sortValue({ name, buckets }: Group, key: SortKey): string | number | null {
  const rows = buckets.filter((row) => row !== undefined);
  if (key === 'name') return name;
  if (key === 'tickets') return rows.reduce((sum, row) => sum + row.tickets, 0);
  if (key === 'aiAccuracy') {
    const total = rows.reduce((sum, row) => sum + row.aiAccuracy.total, 0);
    return total === 0 ? null : rows.reduce((sum, row) => sum + row.aiAccuracy.count, 0) / total;
  }
  const ratings = rows.reduce((sum, row) => sum + row.csat.ratings, 0);
  return ratings === 0 ? null : rows.reduce((sum, row) => sum + (row.csat.average ?? 0) * row.csat.ratings, 0) / ratings;
}

/** One line per bucket, separated by dashed rules. */
function Stack({ values, divided = false, className }: { values: ReactNode[]; divided?: boolean; className?: string }) {
  return (
    <div className={cn('flex flex-col', className)}>
      {values.map((value, index) => (
        <div key={index} className='border-b border-dashed border-gray-500 py-1 first:pt-0 last:border-b-0 last:pb-0'>
          <div className={cn('h-[18px]', divided && DIVIDER)}>{value}</div>
        </div>
      ))}
    </div>
  );
}

export function PerformanceTable({ nameLabel, rows, names, businessAvailable, subRows }: PerformanceTableProps) {
  const { sorted, sortRows, sortProps } = useSortedRows(groupByName(rows, names ?? namesOf(rows)), sortValue);
  const { isExpanded, isClosing, toggle } = useExpandedRows();
  const column = (render: (row: Row | undefined) => ReactNode) => (buckets: Group['buckets']) => buckets.map(render);
  const tickets = column((row) => `${formatCount(row?.tickets ?? 0)} ${row?.tickets === 1 ? 'Ticket' : 'Tickets'}`);
  const aiAccuracy = column((row) => (row?.aiAccuracy.rate != null ? formatPercent(row.aiAccuracy.rate) : '-'));
  const csat = column((row) => (!row || row.csat.average === null ? '-' : `${row.csat.average.toFixed(1)}/ 5`));
  const reopen = column((row) => (row?.reopen.rate != null ? formatPercent(row.reopen.rate) : '-'));
  const ftnr = column((row) => (row?.ftnr.rate != null ? formatPercent(row.ftnr.rate) : '-'));
  const frt = column((row) => averageDuration(row?.firstResponse, businessAvailable));
  const resolution = column((row) => averageDuration(row?.resolution, businessAvailable));
  // Prompt counts only exist for oversight turns.
  const oneToTwo = column((row) => (row?.bucket === 'oversight' ? formatCount(row.oversightPrompts.oneToTwo) : '-'));
  const moreThanTwo = column((row) => (row?.bucket === 'oversight' ? formatCount(row.oversightPrompts.moreThanTwo) : '-'));
  const subHead = 'pt-[3px] pb-1';
  const groupRow = ({ buckets }: Group, label: ReactNode, sub = false, closing = false) => (
    <tr {...(sub ? subRowProps(closing) : {})}>
      <EdgeCell sticky />
      <Cell divider={false} className={cn(STICKY_CELL, 'py-2.5 pr-4 font-medium', sub && SUB_ROW_NAME)}>
        <div className='flex items-center justify-between gap-4'>
          {label}
          <Stack values={BUCKETS.map((bucket) => BUCKET_LABEL[bucket])} className='w-[67px] text-right text-xs font-medium leading-[18px] text-gray-800' />
        </div>
      </Cell>
      <Cell tallDivider className='py-2.5'><Stack values={tickets(buckets)} /></Cell>
      <Cell tallDivider className='py-2.5'><Stack values={aiAccuracy(buckets)} /></Cell>
      <Cell tallDivider className='py-2.5'><Stack values={csat(buckets)} /></Cell>
      <Cell tallDivider className='py-2.5 pr-0'><Stack values={reopen(buckets)} /></Cell>
      <Cell divider={false} className='py-2.5'><Stack values={ftnr(buckets)} divided /></Cell>
      <Cell tallDivider className='py-2.5 pr-0'><Stack values={frt(buckets)} /></Cell>
      <Cell divider={false} className='py-2.5'><Stack values={resolution(buckets)} divided /></Cell>
      <Cell tallDivider className='py-2.5 pr-0'><Stack values={oneToTwo(buckets)} /></Cell>
      <Cell divider={false} className='py-2.5'><Stack values={moreThanTwo(buckets)} divided /></Cell>
      <EdgeCell />
    </tr>
  );

  return (
    <AnalyticsTable>
      <thead>
        <tr>
          <EdgeHead rowSpan={2} sticky />
          <Head rowSpan={2} divider={false} className={cn(STICKY_HEAD, 'w-80 min-w-80')} sort={sortProps('name')}>{nameLabel}</Head>
          <Head rowSpan={2} className='w-40 min-w-40' sort={sortProps('tickets')}>Total Tickets</Head>
          <Head rowSpan={2} className='w-[180px] min-w-[180px]' sort={sortProps('aiAccuracy')}>AI Accuracy Rate</Head>
          <Head rowSpan={2} className='w-[180px] min-w-[180px]' sort={sortProps('csat')}>CSAT Score</Head>
          <GroupHead label='Re-opens' colSpan={2} />
          <GroupHead label='Speed' colSpan={2} />
          <GroupHead label='Oversight' colSpan={2} />
          <EdgeHead rowSpan={2} />
        </tr>
        <tr>
          <Head edge='bottom' divider={false} className={cn(subHead, 'w-[120px] min-w-[120px] pl-2.5')}>Re-Open Rate</Head>
          <Head edge='bottom' className={cn(subHead, 'w-[120px] min-w-[120px]')}>FTNR</Head>
          <Head edge='bottom' divider={false} className={cn(subHead, 'w-[120px] min-w-[120px] pl-2.5')}>FRT</Head>
          <Head edge='bottom' className={cn(subHead, 'w-[120px] min-w-[120px]')}>Resolution Time</Head>
          <Head edge='bottom' divider={false} className={cn(subHead, 'w-[188px] min-w-[188px] pl-2.5')}>1-2 Prompts</Head>
          <Head edge='bottom' className={cn(subHead, 'w-[188px] min-w-[188px]')}>&gt;2 prompts</Head>
        </tr>
      </thead>
      <tbody>
        {sorted.map((group) => {
          const groupSubRows = (subRows && Object.hasOwn(subRows, group.name) ? subRows[group.name] : undefined) ?? [];
          const label = groupSubRows.length > 0 ? <ExpandToggle name={group.name} expanded={isExpanded(group.name)} onToggle={() => toggle(group.name)} /> : <span className='whitespace-normal'>{group.name}</span>;
          return (
            <Fragment key={group.name}>
              {groupRow(group, label)}
              {isExpanded(group.name) || isClosing(group.name) ? sortRows(groupByName(groupSubRows, namesOf(groupSubRows))).map((sub) => <Fragment key={`${group.name}/${sub.name}`}>{groupRow(sub, <span className='whitespace-normal'>{sub.name}</span>, true, isClosing(group.name))}</Fragment>) : null}
            </Fragment>
          );
        })}
      </tbody>
    </AnalyticsTable>
  );
}
